/**
 * Locales. EN and KM ship together from the first merchant UI; ES was
 * added for the US market and awaits native review; TH has a pack and awaits
 * the same; ZH is deferred, but the machinery carries it, so adding it is a
 * pack, not a change.
 *
 * A deferred locale is not a placeholder: its row states the facts the rest of
 * the product reads — how it breaks a line, and whether a thermal printer can
 * render it at all — and those are settled per script, not per pack. Thai was
 * registered ahead of its own pack for exactly that reason: the print path had
 * to know Thai goes through a code page before there was a single Thai string
 * to print.
 *
 * `isLongForm` is a layout fact, not a preference: Khmer runs roughly 30%
 * longer than English and stacks subscript consonants, so a screen that was
 * only ever sized on English is a screen that breaks
 * (`10-build-plan.md` § Risks). It is exported so component tests can assert
 * against the long locale rather than the comfortable one.
 */

type LocaleDefinition = {
  readonly code: string;
  readonly name: string;
  readonly englishName: string;
  readonly direction: "ltr" | "rtl";
  /** Needs a shaping-capable font and generous line-height. */
  readonly isComplexScript: boolean;
  readonly isLongForm: boolean;
  /** Thermal printers cannot render this script from a built-in codepage. */
  readonly requiresRasterReceipt: boolean;
  readonly status: "shipping" | "review_required" | "deferred";
};

const DEFINITIONS = [
  {
    code: "en",
    name: "English",
    englishName: "English",
    direction: "ltr",
    isComplexScript: false,
    isLongForm: false,
    requiresRasterReceipt: false,
    status: "shipping",
  },
  {
    code: "km",
    name: "ភាសាខ្មែរ",
    englishName: "Khmer",
    direction: "ltr",
    isComplexScript: true,
    isLongForm: true,
    requiresRasterReceipt: true,
    status: "review_required",
  },
  {
    code: "es",
    name: "Español",
    englishName: "Spanish",
    direction: "ltr",
    isComplexScript: false,
    // Spanish runs perhaps 20% longer than English — real, but well inside what
    // a screen sized on Khmer already absorbs. Khmer stays the long locale to
    // test against.
    isLongForm: false,
    requiresRasterReceipt: false,
    status: "review_required",
  },
  {
    code: "th",
    name: "ไทย",
    englishName: "Thai",
    direction: "ltr",
    // Thai stacks vowels and tone marks above and below the consonant line and
    // is written without spaces between words, so it needs shaping and a line
    // breaker that knows the language — the same demands Khmer makes.
    isComplexScript: true,
    isLongForm: true,
    // Unlike Khmer, Thai prints as *bytes*. Epson ships seven Thai code pages
    // (20–26) and a `THAI 1 PASS` model font, so a thermal printer renders it
    // from its own ROM at text-mode speed and needs no rasteriser
    // (`pos-print/src/escpos.ts`). This is the one place TOVI's hardest market
    // turns out to be the easier one.
    requiresRasterReceipt: false,
    status: "review_required",
  },
  {
    code: "zh",
    name: "中文",
    englishName: "Chinese",
    direction: "ltr",
    isComplexScript: false,
    isLongForm: false,
    // Thermal printers carry GB18030 on Chinese models, but not reliably on
    // every unit a merchant might own, so the renderer treats it as raster.
    requiresRasterReceipt: true,
    status: "deferred",
  },
] as const satisfies readonly LocaleDefinition[];

export type Locale = (typeof DEFINITIONS)[number]["code"];

export const LOCALES: readonly LocaleDefinition[] = DEFINITIONS;

export const LOCALE_CODES = DEFINITIONS.map((l) => l.code) as unknown as readonly [
  Locale,
  ...Locale[],
];

export const DEFAULT_LOCALE: Locale = "en";

/** The locales a merchant UI offers today. ZH stays out until its pack exists. */
export const ACTIVE_LOCALES: readonly Locale[] = DEFINITIONS.filter(
  (l) => l.status !== "deferred",
).map((l) => l.code);

const BY_CODE = new Map<string, LocaleDefinition>(DEFINITIONS.map((l) => [l.code, l]));

export function isLocale(value: string): value is Locale {
  return BY_CODE.has(value);
}

export function locale(code: Locale): LocaleDefinition {
  const found = BY_CODE.get(code);
  if (!found) throw new Error(`Unknown locale: ${code}`);
  return found;
}

/** Resolves an `Accept-Language` header against the active locales. */
export function negotiateLocale(header: string | null | undefined): Locale {
  if (!header) return DEFAULT_LOCALE;
  const ranked = header
    .split(",")
    .map((part) => {
      const [tag = "", ...params] = part.trim().split(";");
      const q = params.find((p) => p.trim().startsWith("q="));
      const quality = q ? Number.parseFloat(q.trim().slice(2)) : 1;
      return { tag: tag.trim().toLowerCase(), quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of ranked) {
    const base = tag.split("-")[0] ?? tag;
    if (isLocale(base) && ACTIVE_LOCALES.includes(base)) return base;
  }
  return DEFAULT_LOCALE;
}
